import { useState, useReducer, useContext, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import axios from "../../../../interceptors/axiosInstance";
import SuccessModal from "../../../../components/SuccessModal";
import { config } from "../../../../config";
import { CountryList } from "../../../../utils/CountryList";
import { ConfirmModal } from "../../../../components/ConfirmModal/ConfirmModal";
import { LoaderContext } from "../../../../context/LoaderContext";
import {
  emailRegEx,
  whiteSpaceStringRegex,
  phoneNumberRegex,
} from "../../../../utils/ValidationRules";
import { pascalCase } from "../../../../utils/display";
import { MessageContext } from "../../../../context/MessageContext";
import { MultiValueInput } from "../../../../components/General/MultiValueInput/MultiValueInput";
import "./OnboardClient.css";

const initialClientFormState = {
  clientName: "",
  type: "generator",
  location: "Australia",
  personName: "",
  emailAddress: "",
  contactNumber: "",
  taxId: "",
  useEYBenchmarkData: "",
  assets: [],
};

const initialValidityState = {
  clientNameError: "",
  typeError: false,
  locationError: false,
  personNameError: "",
  emailAddressError: "",
  contactNumberError: "",
  taxIdError: "",
  assetsError: "",
  useEYBenchmarkDataError: "",
  isFormValid: false,
};

const formReducer = (state, action) => {
  if (action.type === "RESET_FORM") {
    return initialClientFormState;
  } else if (action.type === "REMOVE_ASSET") {
    const { value } = action.payload;
    return {
      ...state,
      assets: state?.assets?.filter((asset) => asset !== value),
    };
  } else if (action.type === "RESET_ASSETS") {
    return {
      ...state,
      assets: [...initialClientFormState.assets],
    };
  } else if (action.type === "LOAD_URL_STATE") {
    return {
      ...state,
      ...action.payload,
    };
  } else if (action.type === "ENABLE_EY_BENCHMARK_DATA") {
    return {
      ...state,
      useEYBenchmarkData: "yes",
    };
  } else if (action.type === "RESET_EY_BENCHMARK_DATA") {
    return {
      ...state,
      useEYBenchmarkData: "",
    };
  } else {
    const { name, value } = action.type;
    return {
      ...state,
      [name]: name === "assets" ? [...state?.assets, value] : value,
    };
  }
};

const formValidityReducer = (state, action) => {
  let isValid = false;
  const {
    clientName = "",
    personName = "",
    emailAddress = "",
    contactNumber = "",
    taxId = "",
    type = "",
    assets = [],
    useEYBenchmarkData = "",
  } = action.payLoad || {};

  if (action.type === "RESET_FORM_VALIDITY") {
    return action.payLoad;
  } else {
    let errorData = { ...state };
    switch (action.type) {
      case "VALIDATE_CLIENT_NAME":
        isValid =
          clientName.length > 0 && whiteSpaceStringRegex.test(clientName)
            ? true
            : false;
        errorData = {
          ...errorData,
          clientNameError: isValid ? "" : "Enter Client Name",
        };
        break;
      case "VALIDATE_PERSON_NAME":
        isValid =
          personName.length > 0 && whiteSpaceStringRegex.test(personName)
            ? true
            : false;
        errorData = {
          ...errorData,
          personNameError: isValid ? "" : "Enter Person Name",
        };
        break;
      case "VALIDATE_EMAIL":
        isValid =
          emailAddress.length > 0 && emailRegEx.test(emailAddress)
            ? true
            : false;
        errorData = {
          ...errorData,
          emailAddressError: isValid ? "" : "Enter Valid Email",
        };
        break;
      case "VALIDATE_CONTACT":
        isValid =
          contactNumber.length > 0 && phoneNumberRegex.test(contactNumber)
            ? true
            : false;
        errorData = {
          ...errorData,
          contactNumberError: isValid ? "" : "Enter Valid Contact Number",
        };
        break;
      case "VALIDATE_TAXID":
        isValid =
          taxId.length > 0 && whiteSpaceStringRegex.test(taxId) ? true : false;
        errorData = {
          ...errorData,
          taxIdError: isValid ? "" : "Enter Valid Tax Id",
        };
        break;
      case "VALIDATE_ASSETS":
        isValid = assets.length > 0 ? true : false;
        errorData = {
          ...errorData,
          assetsError: isValid ? "" : "Add Asset",
        };
        break;
      case "VALIDATE_USEEYBENCHMARKDATA":
        isValid = useEYBenchmarkData.length > 0 ? true : false;
        errorData = {
          ...errorData,
          useEYBenchmarkDataError: isValid ? "" : "Select An Option",
        };
        break;
      default:
        console.log("Nothing matched");
        break;
    }
    if (type === "generator") {
      const formValidity =
        clientName &&
        personName &&
        emailAddress &&
        contactNumber &&
        taxId &&
        useEYBenchmarkData &&
        assets?.length > 0 &&
        !errorData.clientNameError &&
        !errorData.personNameError &&
        !errorData.emailAddressError &&
        !errorData.contactNumberError &&
        !errorData.taxIdError &&
        !errorData.useEYBenchmarkDataError &&
        !errorData.assetsError;
      errorData.isFormValid = formValidity;
    } else {
      const formValidity =
        clientName &&
        personName &&
        emailAddress &&
        contactNumber &&
        taxId &&
        useEYBenchmarkData &&
        !errorData.clientNameError &&
        !errorData.personNameError &&
        !errorData.emailAddressError &&
        !errorData.contactNumberError &&
        !errorData.taxIdError &&
        !errorData.useEYBenchmarkDataError;
      errorData.isFormValid = formValidity;
    }
    return errorData;
  }
};

export default function OnboardClient({ title }) {
  const [showModal, setShowModal] = useState(false);
  const [showSuccess, setShowScuccess] = useState(false);
  const [clientParams, setClientParams] = useReducer(
    formReducer,
    initialClientFormState,
  );
  const [clientValidity, setClientValidity] = useReducer(
    formValidityReducer,
    initialValidityState,
  );
  const { setLoading } = useContext(LoaderContext);
  const { ErrorToast } = useContext(MessageContext);

  const { state: urlState, pathname } = useLocation();
  const navigate = useNavigate();

  const { type, emailAddress } = clientParams;

  useEffect(() => {
    const identifier = setTimeout(() => {
      if (emailAddress && emailRegEx.test(emailAddress) && type === "admin") {
        const domainIndex = emailAddress.indexOf("@");
        const domainStr = emailAddress.slice(domainIndex + 1);
        if (!domainStr.toLowerCase().includes("ey.com")) {
          setClientParams({ type: { name: "type", value: "generator" } });
          setClientValidity({
            type: "VALIDATE_TYPE",
            payLoad: { ...clientParams, type: "generator" },
          });
          ErrorToast("Only EY Users Can Have Admin Access");
        }
      }
    }, 1000);
    return () => {
      clearTimeout(identifier);
    };
  }, [type, emailAddress]);

  useEffect(() => {
    if (urlState?.id) {
      setClientParams({ type: "LOAD_URL_STATE", payload: urlState });
    }
  }, [urlState]);

  // Effect to reset the form when user moves from update to onboard
  useEffect(() => {
    if (pathname === "/client/onboard" && clientParams.clientName) {
      setClientParams({ type: "RESET_FORM" });
      setClientValidity({
        type: "RESET_FORM_VALIDITY",
        payLoad: initialValidityState,
      });
    }
  }, [pathname]);

  const labels = [
    { label: "Client Name", key: "clientName" },
    { label: "Customer Type", key: "type" },
    { label: "Location", key: "location" },
    { label: "Person Name", key: "personName" },
    { label: "Email Address", key: "emailAddress" },
    { label: "Contact Number", key: "contactNumber" },
    { label: "Tax Id", key: "taxId" },
    {
      label: "Subscribe to EY Benchmark Data",
      key: "useEYBenchmarkData",
    },
  ];
  if (type === "generator") {
    labels.push({ label: "Assets", key: "assets" });
  }

  const handleSubmit = () => {
    setShowModal(true);
  };

  const handleCancel = () => {
    if (urlState?.id) {
      navigate("/client/modify");
    } else {
      setShowModal(false);
      setClientParams({ type: "RESET_FORM" });
      setClientValidity({
        type: "RESET_FORM_VALIDITY",
        payLoad: initialValidityState,
      });
    }
  };

  const submitClientOnboarding = async () => {
    try {
      setLoading(true);
      const res = urlState?.id
        ? await axios.patch(`${config.BASE_URL}/api/client/${urlState?.id}`, {
            type: clientParams?.type,
            assets: clientParams?.assets,
            useEYBenchmarkData: clientParams?.useEYBenchmarkData,
          })
        : await axios.post(`${config.BASE_URL}/api/client`, {
            ...clientParams,
          });
      setShowScuccess(true);
      setShowModal(false);
      if (!urlState?.id) {
        setClientParams({ type: "RESET_FORM" });
        setClientValidity({
          type: "RESET_FORM_VALIDITY",
          payLoad: initialValidityState,
        });
      }
      setLoading(false);
    } catch (err) {
      setShowModal(false);
      setLoading(false);
      if (err.response && err.response.data && err.response.data.message) {
        if (err.response.data.message.trim() === "already exist") {
          ErrorToast("Client is already onboarded with the given Tax ID");
        } else {
          ErrorToast(err.response.data.message);
        }
      } else {
        ErrorToast("Something Went Wrong!");
      }
    }
  };

  const handleChange = (e) => {
    setClientParams({ type: e.target });
    if (e.target.name === "type") {
      setClientValidity({
        type: "VALIDATE_TYPE",
        payLoad: { ...clientParams, type: e.target.value },
      });
      if (e.target.value === "offtaker" || e.target.value === "admin") {
        setClientParams({ type: "RESET_ASSETS" });
      }

      if (e.target.value === "admin") {
        setClientParams({ type: "ENABLE_EY_BENCHMARK_DATA" });
      } else {
        setClientParams({ type: "RESET_EY_BENCHMARK_DATA" });
      }
    } else if (e.target.name === "useEYBenchmarkData") {
      setClientValidity({
        type: "VALIDATE_USEEYBENCHMARKDATA",
        payLoad: { ...clientParams, useEYBenchmarkData: e.target.value },
      });
    }
  };

  const addAsset = (asset) => {
    setClientParams({ type: { name: "assets", value: asset } });
    handleValidation({
      target: { name: "assets", value: [...clientParams?.assets, asset] },
    });
  };

  const removeAsset = (asset) => {
    setClientParams({
      type: "REMOVE_ASSET",
      payload: { name: "assets", value: asset },
    });
    handleValidation({
      target: {
        name: "assets",
        value: clientParams?.assets?.filter((val) => asset !== val),
      },
    });
  };

  const handleValidation = (e) => {
    switch (e.target.name) {
      case "clientName": {
        setClientValidity({
          type: "VALIDATE_CLIENT_NAME",
          payLoad: clientParams,
        });
        break;
      }
      case "personName": {
        setClientValidity({
          type: "VALIDATE_PERSON_NAME",
          payLoad: clientParams,
        });
        break;
      }
      case "emailAddress": {
        setClientValidity({ type: "VALIDATE_EMAIL", payLoad: clientParams });
        break;
      }
      case "contactNumber": {
        setClientValidity({ type: "VALIDATE_CONTACT", payLoad: clientParams });
        break;
      }
      case "taxId": {
        setClientValidity({ type: "VALIDATE_TAXID", payLoad: clientParams });
        break;
      }
      case "assets": {
        setClientValidity({
          type: "VALIDATE_ASSETS",
          payLoad: { ...clientParams, assets: e?.target?.value },
        });
        break;
      }
      default:
        console.log("Nothing matched");
        break;
    }
  };

  return (
    <>
      <div className="admin__client__onboarding__wrapper">
        <div className="admin__client__title">
          {urlState?.id ? `Update ` : `Add `}Client Details
        </div>
        <div className="admin__client__onboarding__form__container">
          <form
            className="admin__client__onboarding__form"
            onSubmit={() => {}}
            autocomplete="off"
          >
            <div className="input_row">
              <div className="admin__client__form__group">
                <label htmlFor="clientName">Client Name *</label>
                <input
                  value={clientParams.clientName}
                  onChange={handleChange}
                  onKeyUp={handleValidation}
                  className="admin__client__input"
                  type="text"
                  id="clientName"
                  name="clientName"
                  placeholder="Enter Name"
                  disabled={!!urlState?.id}
                  required
                />
                {clientValidity.clientNameError && (
                  <span className="admin_input_error">
                    {clientValidity.clientNameError}
                  </span>
                )}
              </div>
              <div className="admin__client__form__group">
                <label htmlFor="type">Customer Type *</label>
                <select
                  className="admin__client__input"
                  id="type"
                  name="type"
                  value={clientParams.type}
                  onChange={handleChange}
                  disabled={!!urlState?.id}
                  required
                >
                  {[
                    { label: "Generator", value: "generator" },
                    { label: "Offtaker", value: "offtaker" },
                    { label: "Admin", value: "admin" },
                  ].map((custtype, index) => (
                    <option id={index} value={custtype.value}>
                      {custtype.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="admin__client__form__group">
                <label htmlFor="location">Location *</label>
                <select
                  className="admin__client__input"
                  id="location"
                  name="location"
                  value={clientParams.location.value}
                  onChange={handleChange}
                  disabled={!!urlState?.id}
                  required
                >
                  {CountryList.map((country, index) => (
                    <option
                      id={index}
                      value={country}
                      selected={country === "Australia" ? true : null}
                    >
                      {country}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="input_row">
              <div className="admin__client__form__group">
                <label htmlFor="personName">Person Name *</label>
                <input
                  value={clientParams.personName}
                  onChange={handleChange}
                  onKeyUp={handleValidation}
                  className="admin__client__input"
                  type="text"
                  id="personName"
                  name="personName"
                  placeholder="Full Name"
                  disabled={!!urlState?.id}
                  required
                />
                {clientValidity.personNameError && (
                  <span className="admin_input_error">
                    {clientValidity.personNameError}
                  </span>
                )}
              </div>
              <div className="admin__client__form__group">
                <label htmlFor="emailAddress">Email Address *</label>
                <input
                  value={clientParams.emailAddress}
                  onChange={handleChange}
                  onKeyUp={handleValidation}
                  className="admin__client__input"
                  type="email"
                  id="emailAddress"
                  name="emailAddress"
                  placeholder="Email Address"
                  disabled={!!urlState?.id}
                  required
                />
                {clientValidity.emailAddressError && (
                  <span className="admin_input_error">
                    {clientValidity.emailAddressError}
                  </span>
                )}
              </div>
              <div className="admin__client__form__group">
                <label htmlFor="contactNumber">Contact Number *</label>
                <input
                  value={clientParams.contactNumber}
                  onChange={handleChange}
                  onKeyUp={handleValidation}
                  className="admin__client__input"
                  type="text"
                  id="contactNumber"
                  name="contactNumber"
                  placeholder="Contact Number"
                  disabled={!!urlState?.id}
                  required
                />
                {clientValidity.contactNumberError && (
                  <span className="admin_input_error">
                    {clientValidity.contactNumberError}
                  </span>
                )}
              </div>
            </div>
            <div className="input_row">
              <div className="admin__client__form__group">
                <label htmlFor="taxId">Tax Id *</label>
                <input
                  value={clientParams.taxId}
                  onChange={handleChange}
                  onKeyUp={handleValidation}
                  className="admin__client__input"
                  type="text"
                  id="taxId"
                  name="taxId"
                  placeholder="Tax Id"
                  disabled={!!urlState?.id}
                  required
                />
                {clientValidity.taxIdError && (
                  <span className="admin_input_error">
                    {clientValidity.taxIdError}
                  </span>
                )}
              </div>
              <div className="admin__client__form__group">
                <label htmlFor="useEYBenchmarkData">
                  Subscribe To EY Benchmark Data *
                </label>
                <select
                  className="admin__client__input"
                  id="useEYBenchmarkData"
                  name="useEYBenchmarkData"
                  value={clientParams.useEYBenchmarkData}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Value</option>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
                {clientValidity.useEYBenchmarkDataError && (
                  <span className="admin_input_error">
                    {clientValidity.useEYBenchmarkDataError}
                  </span>
                )}
              </div>
              {clientParams.type === "generator" && (
                <MultiValueInput
                  onAdd={addAsset}
                  onRemove={removeAsset}
                  values={clientParams.assets}
                />
              )}
            </div>
          </form>
        </div>
        {(title === "Onboard" || title === "Update") && showModal && (
          <ConfirmModal
            params={{ ...clientParams, type: pascalCase(clientParams.type) }}
            data={null}
            toggleModal={setShowModal}
            labels={labels}
            handleSubmit={submitClientOnboarding}
          />
        )}
        {showSuccess ? (
          <SuccessModal
            title={urlState?.id ? `Client Updated` : `Client Onboarded`}
            message={
              urlState?.id
                ? `Client Updated Successfully`
                : `Client Onboarded Successfully!`
            }
            toggleModal={setShowScuccess}
          />
        ) : null}
        <div className="ppa__client__options">
          <button
            disabled={!clientValidity.isFormValid}
            className="forward__option"
            onClick={() => handleSubmit()}
          >
            {title}
          </button>
          <button className="cancel__option" onClick={handleCancel}>
            {urlState?.id ? `Back` : `Cancel`}
          </button>
        </div>
      </div>
    </>
  );
}
